import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Package,
  Plus,
  Trash2,
  Search,
  ExternalLink,
  Loader2,
  AlertCircle,
  Check,
} from 'lucide-react';
import type { Task, TaskAssetLink } from '@/types/task';
import type { Asset } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import { useToast } from '@/hooks/use-toast';
import { dataService } from '@/services/data-service';
import { useQuery } from '@tanstack/react-query';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';

interface TaskDetailLinkedAssetsProps {
  readonly task: Task;
  readonly onTaskUpdate: (task: Task) => void;
  readonly isEditMode?: boolean;
}

function SearchResultsContent({
  isSearching,
  searchQuery,
  searchResults,
  selectedAssets,
  onToggleAsset,
  linkedAssetIds,
}: {
  readonly isSearching: boolean;
  readonly searchQuery: string;
  readonly searchResults: Asset[] | undefined;
  readonly selectedAssets: Asset[];
  readonly onToggleAsset: (asset: Asset) => void;
  readonly linkedAssetIds: Set<string>;
}) {
  if (isSearching) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (searchResults && searchResults.length > 0) {
    return (
      <>
        {searchResults.map((asset: Asset) => {
          const isSelected = selectedAssets.some(a => a.id === asset.id);
          const isAlreadyLinked = linkedAssetIds.has(asset.id);
          
          return (
            <button
              key={asset.id}
              onClick={() => !isAlreadyLinked && onToggleAsset(asset)}
              disabled={isAlreadyLinked}
                className={`w-full p-3 border rounded-lg text-left transition-colors ${
                  (() => {
                    if (isAlreadyLinked) return 'opacity-50 cursor-not-allowed bg-muted';
                    if (isSelected) return 'border-primary bg-accent hover:bg-accent/80';
                    return 'hover:bg-accent';
                  })()
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center flex-shrink-0">
                  {isAlreadyLinked ? (
                    <div className="w-5 h-5 rounded border-2 border-muted-foreground bg-muted flex items-center justify-center">
                      <Check className="h-3 w-3 text-muted-foreground" />
                    </div>
                  ) : (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleAsset(asset)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
                <div className="w-12 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {getAssetPrimaryPhotoUrl(asset) ? (
                    <img
                      src={getAssetPrimaryPhotoUrl(asset)}
                      alt={asset.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate">
                    {asset.name}
                    {isAlreadyLinked && (
                      <span className="ml-2 text-xs text-muted-foreground font-normal">
                        (Already linked)
                      </span>
                    )}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {asset.code}
                  </p>
                  <div className="flex gap-1.5 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {ASSET_STATUS_LABELS[asset.status] || asset.status}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {asset.category}
                    </Badge>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </>
    );
  }

  return (
    <div className="text-center py-8 text-muted-foreground">
      <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
      <p>No assets found matching your search</p>
    </div>
  );
}

export function TaskDetailLinkedAssets({ task, onTaskUpdate, isEditMode = false }: TaskDetailLinkedAssetsProps) {
  const { toast } = useToast();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showAssetDetail, setShowAssetDetail] = useState<TaskAssetLink | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [assetToUnlink, setAssetToUnlink] = useState<TaskAssetLink | null>(null);
  const [assetDetailsCache, setAssetDetailsCache] = useState<Record<string, Asset>>({});

  // Fetch asset details for linked assets that don't have image/status/category
  useEffect(() => {
    const fetchMissingAssetDetails = async () => {
      const assetsToFetch = task.linkedAssets.filter(
        link => link.assetId && !link.assetImageUrl && !assetDetailsCache[link.assetId]
      );
      
      if (assetsToFetch.length === 0) return;
      
      for (const link of assetsToFetch) {
        try {
          const asset = await dataService.getAsset(link.assetId);
          if (asset) {
            setAssetDetailsCache(prev => ({
              ...prev,
              [link.assetId]: asset
            }));
          }
        } catch (error) {
          // Silently fail - asset might not exist or be inaccessible
          console.warn(`Failed to fetch asset details for ${link.assetId}:`, error);
        }
      }
    };
    
    fetchMissingAssetDetails();
  }, [task.linkedAssets]);

  // Enrich linked assets with cached asset details
  const enrichedLinkedAssets = useMemo(() => {
    return task.linkedAssets.map(link => {
      const cachedAsset = assetDetailsCache[link.assetId];
      if (cachedAsset && !link.assetImageUrl) {
        return {
          ...link,
          assetImageUrl: getAssetPrimaryPhotoUrl(cachedAsset) ?? link.assetImageUrl,
          assetStatus: cachedAsset.status ?? link.assetStatus,
          assetCategory: cachedAsset.category ?? link.assetCategory,
          assetName: link.assetName === 'Unknown Asset' ? cachedAsset.name : link.assetName,
          assetCode: link.assetCode ?? cachedAsset.code,
        };
      }
      return link;
    });
  }, [task.linkedAssets, assetDetailsCache]);

  // Search assets - show all assets initially, then filter by search
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ['assets-search', searchQuery],
    queryFn: async () => {
      // Use dataService which respects live/demo mode
      const result = await dataService.getAssets({
        search: searchQuery || undefined,
        pageSize: 100, // Get more results for linking
      });
      return result.data;
    },
    enabled: showLinkDialog, // Enable when dialog is open
  });

  const handleToggleAsset = (asset: Asset) => {
    setSelectedAssets(prev => {
      const isSelected = prev.some(a => a.id === asset.id);
      if (isSelected) {
        return prev.filter(a => a.id !== asset.id);
      } else {
        return [...prev, asset];
      }
    });
  };

  const handleLinkAssets = () => {
    if (selectedAssets.length === 0) return;

    const newLinks: TaskAssetLink[] = selectedAssets.map(asset => ({
      id: `link-${Date.now()}-${asset.id}`,
      assetId: asset.id,
      assetCode: asset.code,
      assetName: asset.name,
      assetImageUrl: getAssetPrimaryPhotoUrl(asset),
      assetStatus: asset.status,
      assetCategory: asset.category,
      linkedAt: new Date().toISOString(),
      linkedBy: 'Current User', // TODO: Get from auth context
    }));

    const updatedTask = {
      ...task,
      linkedAssets: [...task.linkedAssets, ...newLinks],
    };

    onTaskUpdate(updatedTask);
    setShowLinkDialog(false);
    setSearchQuery('');
    setSelectedAssets([]);

    toast({
      title: 'Assets Linked',
      description: `${selectedAssets.length} asset${selectedAssets.length > 1 ? 's have' : ' has'} been linked to this task.`,
    });
  };

  const handleConfirmUnlink = () => {
    if (!assetToUnlink) return;
    
    const updatedTask = {
      ...task,
      linkedAssets: task.linkedAssets.filter(link => link.id !== assetToUnlink.id),
    };

    onTaskUpdate(updatedTask);
    setAssetToUnlink(null);

    toast({
      title: 'Asset Removed',
      description: `${assetToUnlink.assetName || 'Asset'} has been removed from this task. Remember to save your changes.`,
    });
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'disposed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <>
      <Card className="border border-border/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Linked Assets
              </CardTitle>
              <CardDescription className="text-xs">
                Assets associated with this task
              </CardDescription>
            </div>
          {isEditMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLinkDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Link Asset
            </Button>
          )}
          </div>
        </CardHeader>
      <CardContent>
      {enrichedLinkedAssets.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Linked Assets</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Link assets to this task to track related equipment and resources
          </p>
          {isEditMode && (
            <Button
              variant="outline"
              onClick={() => setShowLinkDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Link Your First Asset
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {enrichedLinkedAssets.map((link) => (
            <div
              key={link.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow relative group"
            >
              {isEditMode && (
                <button
                  onClick={() => setAssetToUnlink(link)}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-destructive/90 text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                  title="Remove asset"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}

              <div className="flex items-start gap-3">
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {link.assetImageUrl ? (
                    <img
                      src={link.assetImageUrl}
                      alt={link.assetName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate mb-1">
                    {link.assetName}
                  </h4>
                  {link.assetCode && (
                    <p className="text-xs text-muted-foreground mb-2">
                      {link.assetCode}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {link.assetStatus && (
                      <Badge
                        variant="outline"
                        className={`text-xs ${getStatusColor(link.assetStatus)}`}
                      >
                        {link.assetStatus}
                      </Badge>
                    )}
                    {link.assetCategory && (
                      <Badge variant="secondary" className="text-xs">
                        {link.assetCategory}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3"
                onClick={() => setShowAssetDetail(link)}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          ))}
        </div>
      )}
      </CardContent>
      </Card>
      
      {/* Link Asset Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-2xl bg-background">
          <DialogHeader>
            <DialogTitle>Link Assets to Work Item</DialogTitle>
            <DialogDescription>
              Browse all assets or search to find assets to link to this task. Select multiple assets to link them all at once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by asset name, code, or serial number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              {selectedAssets.length > 0 && (
                <Badge variant="secondary" className="ml-3">
                  {selectedAssets.length} selected
                </Badge>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              <SearchResultsContent
                isSearching={isSearching}
                searchQuery={searchQuery}
                searchResults={searchResults}
                selectedAssets={selectedAssets}
                onToggleAsset={handleToggleAsset}
                linkedAssetIds={new Set(task.linkedAssets.map(link => link.assetId))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkDialog(false);
                setSearchQuery('');
                setSelectedAssets([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkAssets}
              disabled={selectedAssets.length === 0}
            >
              Link {selectedAssets.length > 0 ? `${selectedAssets.length} ` : ''}Asset{selectedAssets.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asset Detail Dialog */}
      <Dialog open={!!showAssetDetail} onOpenChange={() => setShowAssetDetail(null)}>
        <DialogContent className="max-w-2xl bg-background">
          <DialogHeader>
            <DialogTitle>Asset Details</DialogTitle>
          </DialogHeader>

          {showAssetDetail && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {showAssetDetail.assetImageUrl ? (
                    <img
                      src={showAssetDetail.assetImageUrl}
                      alt={showAssetDetail.assetName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Package className="h-12 w-12 text-muted-foreground" />
                  )}
                </div>

                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">
                    {showAssetDetail.assetName}
                  </h3>
                  {showAssetDetail.assetCode && (
                    <p className="text-sm text-muted-foreground mb-3">
                      Code: {showAssetDetail.assetCode}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {showAssetDetail.assetStatus && (
                      <Badge
                        variant="outline"
                        className={getStatusColor(showAssetDetail.assetStatus)}
                      >
                        {showAssetDetail.assetStatus}
                      </Badge>
                    )}
                    {showAssetDetail.assetCategory && (
                      <Badge variant="secondary">
                        {showAssetDetail.assetCategory}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Link Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Linked By:</span>
                    <span className="font-medium">{showAssetDetail.linkedBy}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Linked At:</span>
                    <span className="font-medium">
                      {new Date(showAssetDetail.linkedAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={() => {
                  window.open(`/assets/${showAssetDetail.assetId}`, '_blank');
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Asset in New Tab
              </Button>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <AlertDialog open={!!assetToUnlink} onOpenChange={(open) => !open && setAssetToUnlink(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Linked Asset?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <span className="font-semibold">{assetToUnlink?.assetName || 'this asset'}</span> from this task?
              {assetToUnlink?.assetCode && (
                <span className="block mt-1 text-muted-foreground">
                  Asset Code: {assetToUnlink.assetCode}
                </span>
              )}
              <span className="block mt-2">
                This action will be applied when you save the task.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUnlink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Asset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
